import { useEffect, useRef } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { VoicesTopicStatus } from "../Enums/voices";
import {
  createVoicesTopic,
  deleteVoicesTopic,
  getVoicesRadarStatus,
  getVoicesTopics,
  setVoicesTopicStatus,
  startVoicesRadar,
  updateVoicesTopic,
} from "../Api/voicesTopics";
import {
  VoicesRadarStatus,
  VoicesTopicFilter,
  VoicesTopicPage,
  VoicesTopicPayload,
} from "../Models/voicesTopics";

const RADAR_POLL_MS = 5000;

export const useVoicesTopics = (campaignId: number, filter: VoicesTopicFilter, limit: number) => {
  return useQuery<VoicesTopicPage, Error>({
    queryKey: ["voicesTopics", campaignId, filter, limit],
    queryFn: () => getVoicesTopics(campaignId, filter, limit),
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
  });
};

const useTopicMutation = <TVariables, TResult>(
  campaignId: number,
  mutationFn: (variables: TVariables) => Promise<TResult>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voicesTopics", campaignId] });
    },
  });
};

export const useSaveVoicesTopic = (campaignId: number) =>
  useTopicMutation(
    campaignId,
    (variables: { topicId: number | null; data: VoicesTopicPayload }) =>
      variables.topicId === null
        ? createVoicesTopic(campaignId, variables.data)
        : updateVoicesTopic(campaignId, variables.topicId, variables.data),
  );

export const useSetVoicesTopicStatus = (campaignId: number) =>
  useTopicMutation(
    campaignId,
    (variables: { topicId: number; status: VoicesTopicStatus }) =>
      setVoicesTopicStatus(campaignId, variables.topicId, variables.status),
  );

export const useDeleteVoicesTopic = (campaignId: number) =>
  useTopicMutation(campaignId, (topicId: number) =>
    deleteVoicesTopic(campaignId, topicId),
  );

export const useVoicesRadar = (campaignId: number, enabled: boolean) => {
  const queryClient = useQueryClient();
  const status = useQuery<VoicesRadarStatus, Error>({
    queryKey: ["voicesRadar", campaignId],
    queryFn: () => getVoicesRadarStatus(campaignId),
    enabled: enabled && !!campaignId,
    refetchInterval: (query) => (query.state.data?.running ? RADAR_POLL_MS : false),
  });
  const running = !!status.data?.running;
  const wasRunning = useRef(running);

  useEffect(() => {
    if (wasRunning.current && !running) {
      queryClient.invalidateQueries({ queryKey: ["voicesTopics", campaignId] });
    }
    wasRunning.current = running;
  }, [running, campaignId, queryClient]);

  const start = useMutation({
    mutationFn: () => startVoicesRadar(campaignId),
    onSuccess: (data) => queryClient.setQueryData(["voicesRadar", campaignId], data),
    onError: () => queryClient.invalidateQueries({ queryKey: ["voicesRadar", campaignId] }),
  });

  return { running, start };
};
